'use client';

import { useEffect } from 'react';

import { parseJWT } from '@storefront/app/api/auth/[...nextauth]/auth.utils';
import {
  BundleCartItem,
  BundleProduct,
  GetCartItemsQuery,
  GetCustomerCartQuery,
  SelectedBundleOption,
  VirtualProduct,
} from '@storefront/data-commerce';
import {
  InsideOrderData,
  InsidePageProps,
} from '@storefront/types/insideData.d';
import { getProductTypeLabel } from '@storefront/utils/prodRequests';
import { getSession } from 'next-auth/react';

const fillCartData = (
  cart: GetCustomerCartQuery['customerCart'] | GetCartItemsQuery['cart']
) => {
  window._insideData.cart = { id: '', items: [], price: 0 };
  window._insideData.cart.id = cart?.id;
  /// we may have Paper plan as other product in the cart, ,so we need to find the option that has bundle_options
  const otherItemsCartItems = cart?.items?.filter(
    (item) => (item as BundleCartItem)?.bundle_options
  );
  const otherItems = (otherItemsCartItems[0] as { [key: string]: any })?.[
    'bundle_options'
  ]?.map((bundleOption: SelectedBundleOption) => ({
    name: bundleOption?.label,
    qty: 1,
    sku: bundleOption?.values?.[0]?.sku,
    category: 'bundleOption',
  }));
  window._insideData.cart.items = cart?.items
    .map((item: any) => ({
      img: item?.product?.thumbnail?.url,
      name: item.product.name,
      qty: item.quantity,
      sku: item.product.sku,
      category:
        getProductTypeLabel(item.product as BundleProduct) ||
        (item.product as VirtualProduct)?.hp_product_type_label,
    }))
    .concat(otherItems);

  window._insideData.cart.price = cart.prices.grand_total.value;
  return window._insideData;
};

const fillUserData = () => {
  getSession()
    .then((session) => {
      if (session) {
        const { family_name, given_name } = parseJWT(session['id_token']);
        window._insideData.user = {
          email: session.user.email,
          name: given_name + ' ' + family_name,
        };
      }
    })
    .catch(() => (window._insideData.user = undefined));
};

const fillProductsData = (product: BundleProduct) => {
  window._insideData.product = [
    {
      name: product.name,
      price: product.price_range.minimum_price.regular_price.value,
      sku: product.sku,
      category: getProductTypeLabel(product),
      img: product.image.url,
    },
  ];
};

const fillWebsiteData = () => {
  window._insideData.website = {
    country: 'US',
    currency: 'USD',
    language: 'EN',
  };
};

const fillPurchaseOrderData = (order: InsideOrderData) => {
  const orderTotal = order?.ordersData?.customer?.orders?.items?.[0]?.total;
  window._insideData.order = {
    id: order?.orderId,
    shipping: orderTotal?.shipping_handling?.amount_including_tax?.value,
    tax: orderTotal?.taxes[0]?.amount?.value,
    total: orderTotal?.grand_total?.value,
  };
};

const fillCurrentPageData = (page: InsidePageProps) => {
  window._insideData.page = {
    name: page.name,
    type: page.type,
  };
};

interface UseInsideDataProps {
  cart?: GetCustomerCartQuery['customerCart'] | GetCartItemsQuery['cart'];
  page?: InsidePageProps;
  order?: InsideOrderData;
  product?: BundleProduct;
}

export const useInsideData = ({
  cart,
  order,
  page,
  product,
}: UseInsideDataProps) => {
  useEffect(() => {
    window._insideData = {};
    fillUserData();
    fillWebsiteData();
  }, []);

  useEffect(() => {
    if (!window._insideData) return;
    if (product) fillProductsData(product);
    if (cart) fillCartData(cart);
    if (order) fillPurchaseOrderData(order);
    if (page) fillCurrentPageData(page);
  }, [product, cart, order, page]);

  return typeof window === 'undefined' ? undefined : window._insideData;
};

export default useInsideData;
